import { memo, useMemo } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import classNames from 'classnames';

import { Breakpoints, getMediaQuery } from '../../../utils/breakpoints';
import { getPropsFromModel, getComponentFromProps } from '../../../utils/use-child-components';
import { useDragComponentsPlaceholder } from '../../../utils/use-drag-components-placeholder';

import { LogoContentContainerProps, NUM_OF_ITEMS_ON_MOBILE } from './logo-content-container.config';
import { ContainerBackgroundVideo } from '../../container/container-background-video/container-background-video';

const LogoContentContainer = memo(
  ({
    accessibilityLabel,
    additionalClasses,
    backgroundColor,
    cqItems,
    cqItemsOrder,
    cqPath,
    fallbackImage,
    id,
    isInEditor,
    opacity,
    overlayColor,
    roleAttribute,
    video,
  }: LogoContentContainerProps) => {
    const isNotMobile = useMediaQuery(getMediaQuery(Breakpoints.MOBILE, 'up'));
    const { placeholder } = useDragComponentsPlaceholder({ cqPath, isInEditor });

    const slicedCqItemsOrder = useMemo(
      () => cqItemsOrder.slice(0, !isNotMobile ? NUM_OF_ITEMS_ON_MOBILE : undefined),
      [cqItemsOrder, isNotMobile]
    );

    return (
      <div
        aria-label={accessibilityLabel}
        className={classNames('snowflake-logo-content-container', additionalClasses)}
        data-testid="snowflake-logo-content-container"
        id={id}
        role={roleAttribute}
        style={{ backgroundColor }}
      >
        <div className="snowflake-logo-content-container-inner" data-testid="snowflake-logo-content-container-inner">
          {slicedCqItemsOrder.map((cqItem) => {
            const cqItemProps = cqItems[cqItem];
            const logoItemProps = getPropsFromModel(cqItemProps, cqPath, cqItem);
            const LogoItem = getComponentFromProps(logoItemProps);

            return (
              LogoItem && (
                <div
                  className="snowflake-logo-content-container-item"
                  data-testid="snowflake-logo-content-container-item"
                  key={cqItem}
                >
                  <LogoItem {...logoItemProps} />
                </div>
              )
            );
          })}
        </div>
        {placeholder}
        <ContainerBackgroundVideo
          fallbackImage={fallbackImage}
          isInEditor={isInEditor}
          opacity={opacity}
          overlayColor={overlayColor}
          video={video}
        />
      </div>
    );
  }
);

LogoContentContainer.displayName = 'LogoContentContainer';

export default LogoContentContainer;
